import React, { useEffect, useState } from "react";
import { ContentCopy } from "@styled-icons/material/ContentCopy";
import { Check } from "@styled-icons/material/Check";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import styled from "styled-components/macro";

import LoadingCard from "../../components/LoadingCard";
import ReferralsEmptyState, { PARTNERSHIPS_EMAIL } from "./ReferralsEmptyState";
import { loadReferralsData } from "./utils/api";
import useCopyToClipboard from "../../hooks/useCopyToClipboard";
import { trackReferralsTabOpened } from "../../utils/analyticsUtils";
import { mailtoHref } from "../../utils/textUtils";
import { mediaQueries, mediaQueriesUntil } from "../../styles/media.js";

// The estate agent's leads from the last 90 days, as served by /api/dashboard/referrals. The
// server decides who may see what and the default order (contacting, instructed, not proceeding,
// newest first within each). This view filters the list and re-sorts it by column.

// outcome_group values, in the order the summary cards show them.
export const STATUS_GROUPS = [
  { key: "contacting", label: "Contacting" },
  { key: "instructed", label: "Instructed" },
  { key: "not_proceeding", label: "Not proceeding" },
];

const statusLabel = (group) => STATUS_GROUPS.find((s) => s.key === group)?.label ?? group;

const LEAD_TYPE_LABELS = {
  buying: "Buying",
  selling: "Selling",
  buying_and_selling: "Buying & selling",
};

// Agent-facing wording for the outcome buckets the server serves (which mirror Looker's).
// Anything unlisted is shown as served.
const OUTCOME_LABELS = {
  "Lead chose another firm": "Chose another firm",
  "Lead was a wrong number": "Wrong number",
  "Juno could not get hold of lead": "We couldn’t reach them",
  "Lead not ready to instruct": "Not ready to instruct",
  "Juno at capacity for lead type": "We were at capacity",
  "Price differential": "Price",
  "Lead waiting until offer": "Waiting for an offer",
  "Lead no longer transacting": "No longer moving",
  "Juno does not offer required service": "Service not offered",
};

const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());
const DAY_MS = 24 * 60 * 60 * 1000;

// ["18 Aug", "12:29"]. Today, yesterday and tomorrow (a booked call) are named instead of dated,
// and the year only shows when it differs from this one: the window is 90 days, so that is only
// around the turn of the year.
export const formatReferralDateTime = (datetime, now = new Date()) => {
  if (datetime == null) {
    return null;
  }
  const date = new Date(datetime);
  const time = date.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  });
  const daysAway = Math.round((startOfDay(date) - startOfDay(now)) / DAY_MS);
  const relative = { "-1": "Yesterday", 0: "Today", 1: "Tomorrow" }[daysAway];
  if (relative) {
    return [relative, time];
  }
  const day = date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    ...(date.getFullYear() !== now.getFullYear() && { year: "numeric" }),
  });
  return [day, time];
};

// Date first so the column lines up; the two parts sit on one line and stack on a phone.
const DateTime = ({ value }) => {
  const parts = formatReferralDateTime(value);
  if (parts == null) {
    return null;
  }
  return (
    <>
      <DatePart>{parts[0]},</DatePart> <DatePart>{parts[1]}</DatePart>
    </>
  );
};

DateTime.propTypes = {
  value: PropTypes.string,
};

// "£430k", "£1.25m": compact so a buy-and-sell pair stays on one line.
const trimZeros = (n) => n.toFixed(2).replace(/\.?0+$/, "");
export const formatPrice = (pounds) =>
  pounds >= 1_000_000 ? `£${trimZeros(pounds / 1_000_000)}m` : `£${Math.round(pounds / 1000)}k`;

// Price and type in one cell: "£430k P", "£300k S", or both for a buy-and-sell lead,
// "£430k P / £300k S", dropping a side whose price is unknown. With no price at all the type
// stands alone ("Buying & selling").
export const formatReferralPrices = ({ lead_type, purchase_price, sale_price }) => {
  const parts = [
    purchase_price != null && `${formatPrice(purchase_price)} P`,
    sale_price != null && `${formatPrice(sale_price)} S`,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(" / ") : (LEAD_TYPE_LABELS[lead_type] ?? "");
};

// The server sends E.164 for the tel: link; UK numbers are shown in national form, unspaced so
// they paste cleanly into a dialler. Anything else is shown as sent.
export const formatPhoneForDisplay = (phone) => {
  const uk = /^\+44(\d{9,10})$/.exec(phone);
  return uk ? `0${uk[1]}` : phone;
};

// The address line one, or the client's name when the case has no address.
export const shortCaseLabel = (label) => label.split(",")[0].trim();

// "P: 14 Orchard Way" for a purchase, "S: …" for a sale, matching the P/S in the price
// column; any other case type falls back to its initial.
const CASE_TYPE_LABELS = { buying: "P", selling: "S" };
const caseTypeLabel = (caseType) => CASE_TYPE_LABELS[caseType] ?? caseType.charAt(0).toUpperCase();

// "Luke S.": who on the branch sent the lead, short enough to keep the row on one line.
export const formatSentBy = (agentName) => {
  const words = (agentName ?? "").trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) {
    return "";
  }
  const first = words[0].charAt(0).toUpperCase() + words[0].slice(1);
  return words.length === 1 ? first : `${first} ${words.at(-1).charAt(0).toUpperCase()}.`;
};

const statusRank = Object.fromEntries(STATUS_GROUPS.map(({ key }, i) => [key, i]));
const toTime = (datetime) => datetime && new Date(datetime).getTime();

// The value each sortable column sorts by.
const SORT_KEYS = {
  client: (r) => r.client_name,
  sentBy: (r) => r.agent_name?.trim(),
  referred: (r) => toTime(r.created_at),
  lastContact: (r) => toTime(r.last_contact_time),
  status: (r) => statusRank[r.outcome_group],
};

// Dates sort newest first on the first click; everything else A to Z, or the server's status order.
const NEWEST_FIRST = ["referred", "lastContact"];

const isBlank = (value) => value == null || value === "";

// Blank values go last either way. Array sort is stable, so ties keep the server's order.
const sortReferrals = (referrals, { column, direction }) => {
  const key = SORT_KEYS[column];
  const sign = direction === "asc" ? 1 : -1;
  return [...referrals].sort((x, y) => {
    const a = key(x);
    const b = key(y);
    if (isBlank(a) || isBlank(b)) {
      return isBlank(a) - isBlank(b);
    }
    return sign * (typeof a === "string" ? a.localeCompare(b, "en-GB", { sensitivity: "base" }) : a - b);
  });
};

const SortableHeader = ({ column, label, sort, onSort }) => {
  const active = sort.column === column;
  const ariaSort = active ? (sort.direction === "asc" ? "ascending" : "descending") : "none";
  return (
    <th aria-sort={ariaSort}>
      <SortButton type="button" onClick={() => onSort(column)} $active={active}>
        {label}
        {active && <SortArrow aria-hidden="true">{sort.direction === "asc" ? "▲" : "▼"}</SortArrow>}
      </SortButton>
    </th>
  );
};

SortableHeader.propTypes = {
  column: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  sort: PropTypes.shape({ column: PropTypes.string, direction: PropTypes.string }).isRequired,
  onSort: PropTypes.func.isRequired,
};

// Agents mostly paste contact details into their own systems, so each has a copy button.
const CopyButton = ({ value, label }) => {
  const [copied, copy] = useCopyToClipboard(1500);
  return (
    <CopyControl type="button" onClick={() => copy(value)} aria-label={`${copied ? "Copied" : "Copy"} ${label}`}>
      {copied ? <Check aria-hidden="true" /> : <ContentCopy aria-hidden="true" />}
    </CopyControl>
  );
};

CopyButton.propTypes = {
  value: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
};

const PhoneContact = ({ phone, clientName }) => (
  <Contact>
    <ContactLink href={`tel:${phone}`}>{formatPhoneForDisplay(phone)}</ContactLink>
    <CopyButton value={formatPhoneForDisplay(phone)} label={`${clientName}’s phone number`} />
  </Contact>
);

PhoneContact.propTypes = {
  phone: PropTypes.string.isRequired,
  clientName: PropTypes.string.isRequired,
};

const EmailContact = ({ email, clientName }) => {
  const href = mailtoHref(email);
  return (
    <Contact>
      <EmailText title={email}>{href ? <ContactLink href={href}>{email}</ContactLink> : email}</EmailText>
      <CopyButton value={email} label={`${clientName}’s email address`} />
    </Contact>
  );
};

EmailContact.propTypes = {
  email: PropTypes.string.isRequired,
  clientName: PropTypes.string.isRequired,
};

// The Notes column: why a lead is not proceeding, or where we are with one we are still chasing.
// A booked callback and an instructed lead's cases are rendered by the row instead.
export const referralNote = (referral) => {
  switch (referral.outcome_group) {
    case "not_proceeding":
      return OUTCOME_LABELS[referral.outcome] ?? referral.outcome;
    case "contacting":
      if (referral.contact_count === 0) {
        return "Not contacted yet";
      }
      return referral.last_contact_status ?? "";
    default:
      return "";
  }
};

const Notes = ({ referral }) => {
  if (referral.outcome_group === "contacting" && referral.callback_booked && referral.next_call_time) {
    return (
      <>
        Call: <DateTime value={referral.next_call_time} />
      </>
    );
  }
  if (referral.outcome_group === "instructed") {
    if (!referral.cases?.length) {
      return "No case to open";
    }
    return referral.cases.map((c, i) => (
      <React.Fragment key={c.id}>
        {i > 0 && <CaseSeparator>·</CaseSeparator>}
        <CaseLink to={{ pathname: "/dashboard", search: `?search=${encodeURIComponent(c.case_reference)}` }}>
          {caseTypeLabel(c.case_type)}: {shortCaseLabel(c.label)}
        </CaseLink>
      </React.Fragment>
    ));
  }
  return referralNote(referral);
};

Notes.propTypes = {
  referral: PropTypes.object.isRequired,
};

// Comma-separated terms must all match, as the case search does: client name, status, case ref.
// Matches the number as stored (+447700900123) and as shown (07700900123), ignoring spaces.
const phoneMatches = (phone, part) => {
  if (!phone) return false;
  const needle = part.replace(/\s+/g, "");
  return needle !== "" && (phone.includes(needle) || formatPhoneForDisplay(phone).includes(needle));
};

// Matches the client's name, phone and email, the status label, and the reference or address of
// any converted case, so an agent can find a lead from whatever they have to hand.
export const filterReferralsBySearchInput = (referrals, searchInput) => {
  if (!searchInput) {
    return referrals;
  }
  const searchParts = searchInput
    .toLowerCase()
    .split(",")
    .map((part) => part.trim())
    .filter((part) => part !== "");
  return referrals.filter((r) =>
    searchParts.every(
      (part) =>
        r.client_name?.toLowerCase().includes(part) ||
        r.client_email?.toLowerCase().includes(part) ||
        phoneMatches(r.client_phone, part) ||
        statusLabel(r.outcome_group).toLowerCase().includes(part) ||
        r.cases?.some((c) => c.case_reference?.toLowerCase().includes(part) || c.label?.toLowerCase().includes(part)),
    ),
  );
};

const ReferralsView = ({ searchInput, referralLink, onClearSearch }) => {
  const dispatch = useDispatch();
  const { status, data } = useSelector((state) => state.dashboardState.referrals);
  const [statusFilter, setStatusFilter] = useState(null);
  const [sort, setSort] = useState({ column: "status", direction: "asc" });
  const [loadAttempt, setLoadAttempt] = useState(0);

  useEffect(() => {
    trackReferralsTabOpened();
  }, []);

  // Fetched every time the tab opens (and on retry). Data already in the store stays on screen
  // while the fresh list arrives.
  useEffect(() => {
    const controller = new AbortController();
    loadReferralsData(dispatch, controller.signal);
    return () => controller.abort();
  }, [dispatch, loadAttempt]);

  const retry = () => setLoadAttempt((n) => n + 1);

  if (data == null) {
    if (status === "failed") {
      return (
        <Wrapper>
          <ReferralsEmptyState kind="failed" onRetry={retry} />
        </Wrapper>
      );
    }
    return (
      <Wrapper>
        <LoadingCard />
      </Wrapper>
    );
  }

  const referrals = data.referrals ?? [];
  const branchView = data.referral_code_visibility === "branch";

  const searched = filterReferralsBySearchInput(referrals, searchInput);
  const counts = Object.fromEntries(
    STATUS_GROUPS.map(({ key }) => [key, searched.filter((r) => r.outcome_group === key).length]),
  );

  const filtered = statusFilter ? searched.filter((r) => r.outcome_group === statusFilter) : searched;
  const rows = sortReferrals(filtered, sort);

  // Clicking the sorted column reverses it.
  const onSort = (column) =>
    setSort((current) =>
      current.column === column
        ? { column, direction: current.direction === "asc" ? "desc" : "asc" }
        : { column, direction: NEWEST_FIRST.includes(column) ? "desc" : "asc" },
    );

  const renderEmptyState = () => {
    if (referrals.length === 0) {
      return <ReferralsEmptyState kind={branchView ? "none" : "own_only"} referralLink={referralLink} />;
    }
    if (searched.length === 0) {
      return <ReferralsEmptyState kind="search" searchInput={searchInput} onClearSearch={onClearSearch} />;
    }
    return (
      <ReferralsEmptyState
        kind="filter"
        statusLabel={statusLabel(statusFilter)}
        searchInput={searchInput}
        onShowAll={() => {
          setStatusFilter(null);
          if (searchInput) onClearSearch();
        }}
      />
    );
  };

  return (
    <Wrapper>
      <h1>Your referrals (last 90 days)</h1>

      <SummaryRow>
        <SummaryCard
          $selected={statusFilter == null}
          aria-pressed={statusFilter == null}
          onClick={() => setStatusFilter(null)}
        >
          <SummaryNumber>{searched.length}</SummaryNumber>
          <SummaryLabel>Referrals</SummaryLabel>
        </SummaryCard>
        {STATUS_GROUPS.map(({ key, label }) => (
          <SummaryCard
            key={key}
            $selected={statusFilter === key}
            aria-pressed={statusFilter === key}
            onClick={() => setStatusFilter(statusFilter === key ? null : key)}
          >
            <SummaryNumber $status={key}>{counts[key]}</SummaryNumber>
            <SummaryLabel>{label}</SummaryLabel>
          </SummaryCard>
        ))}
      </SummaryRow>

      {status === "failed" && (
        <RefreshFailed role="alert">
          We couldn’t refresh your referrals, so this list may be out of date.{" "}
          <button type="button" onClick={retry}>
            Try again
          </button>
        </RefreshFailed>
      )}

      {referrals.length > 0 && (
        <ScopeNote>
          {branchView ? (
            "Includes referrals your colleagues submitted under your branch’s referral code."
          ) : (
            <>
              We only show referrals you submitted yourself. To see your whole branch’s, email{" "}
              <a href={`mailto:${PARTNERSHIPS_EMAIL}`}>{PARTNERSHIPS_EMAIL}</a>.
            </>
          )}
        </ScopeNote>
      )}

      {rows.length === 0 ? (
        renderEmptyState()
      ) : (
        <TableCard>
          <TableScroll>
            <Table>
              <thead>
                <tr>
                  <SortableHeader column="client" label="Client" sort={sort} onSort={onSort} />
                  <SortableHeader column="status" label="Status" sort={sort} onSort={onSort} />
                  <th>Phone</th>
                  <th>Email</th>
                  <SortableHeader column="referred" label="Referred" sort={sort} onSort={onSort} />
                  <SortableHeader column="sentBy" label="Sent by" sort={sort} onSort={onSort} />
                  <th>Price (type)</th>
                  <SortableHeader column="lastContact" label="Last contact" sort={sort} onSort={onSort} />
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <Row key={r.id} $status={r.outcome_group}>
                    <ClientCell data-cell="client">
                      <ClientName title={r.client_name}>{r.client_name}</ClientName>
                    </ClientCell>
                    <td data-cell="status">
                      <StatusPill $status={r.outcome_group}>{statusLabel(r.outcome_group)}</StatusPill>
                    </td>
                    <td data-cell="phone">
                      {r.client_phone && <PhoneContact phone={r.client_phone} clientName={r.client_name} />}
                    </td>
                    <td data-cell="email">
                      {r.client_email && <EmailContact email={r.client_email} clientName={r.client_name} />}
                    </td>
                    <td data-cell="referred" data-title="Referred">
                      {formatReferralDateTime(r.created_at)[0]}
                    </td>
                    <td data-cell="sent-by" data-title="Sent by">
                      {formatSentBy(r.agent_name)}
                    </td>
                    <td data-cell="price">{formatReferralPrices(r)}</td>
                    <td data-cell="last-contact" data-title="Last contact">
                      <DateTime value={r.last_contact_time} />
                    </td>
                    <td data-cell="notes">
                      <Notes referral={r} />
                    </td>
                  </Row>
                ))}
              </tbody>
            </Table>
          </TableScroll>
        </TableCard>
      )}
    </Wrapper>
  );
};

export default ReferralsView;

ReferralsView.propTypes = {
  searchInput: PropTypes.string,
  referralLink: PropTypes.string,
  onClearSearch: PropTypes.func.isRequired,
};

const Wrapper = styled.div`
  /* Values with no equivalent on the juno scale; kept so the design does not move. */
  --referrals-row-padding: 0.85rem;
  --referrals-cell-gutter: 0.375rem;
  --referrals-gap: 0.35rem;
  --referrals-block-gap: 0.6rem;
  --referrals-line-gap: 0.25rem;
  --referrals-separator-gap: 0.4rem;
  --referrals-summary-padding: 0.6rem;
  --referrals-header-padding: 0.7rem;
  --referrals-pill-padding: 0.1rem 0.6rem;
  --referrals-copy-padding: 0.1rem;
  --referrals-font-size-xs: 0.68rem;
  --referrals-font-size-sm: 0.8rem;
  --referrals-font-size-md: 0.9rem;
  --referrals-font-size-pill: 0.78rem;

  background: var(--juno-color-background-grey);
  border-top: 1px solid #ebebeb;
  min-height: 100%;
  padding: var(--juno-space-6) var(--juno-space-5);

  ${mediaQueries.md} {
    padding: var(--juno-space-6) var(--juno-space-4);
  }

  h1 {
    font-size: var(--juno-font-size-h3-md);
    font-weight: var(--juno-font-weight-bold);
    margin-bottom: var(--juno-space-5);
  }
`;

const SummaryRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--juno-space-3);
  margin-bottom: var(--juno-space-5);

  ${mediaQueries.md} {
    grid-template-columns: repeat(4, 1fr);
    max-width: 40rem;
  }
`;

const SummaryCard = styled.button`
  background: var(--juno-color-surface);
  border: 1px solid ${(props) => (props.$selected ? "var(--juno-color-purple)" : "var(--juno-color-pale-grey)")};
  ${(props) => props.$selected && "box-shadow: 0 0 0 1px var(--juno-color-purple);"}
  border-radius: var(--juno-radius-lg);
  padding: var(--referrals-summary-padding) var(--juno-space-4);
  text-align: left;
  cursor: pointer;
  font-family: inherit;

  &:hover {
    border-color: var(--juno-color-purple);
  }
`;

const statusColour = (status) => {
  if (status === "instructed") return "var(--juno-status-color-referral-instructed)";
  if (status === "contacting") return "var(--juno-status-color-referral-contacting)";
  if (status === "not_proceeding") return "var(--juno-status-color-referral-not-proceeding)";
  return "var(--juno-color-off-black)";
};

const SummaryNumber = styled.div`
  font-size: var(--juno-font-size-h3-sm);
  font-weight: var(--juno-font-weight-bold);
  color: ${(props) => statusColour(props.$status)};
`;

const SummaryLabel = styled.div`
  font-size: var(--referrals-font-size-sm);
  color: var(--juno-color-text-secondary);
`;

const ScopeNote = styled.p`
  font-size: var(--juno-font-size-body-sm);
  color: var(--juno-color-text-secondary);
  margin: 0 0 var(--juno-space-3);

  a {
    color: var(--juno-color-purple);
    font-weight: var(--juno-font-weight-bold);
  }
`;

const RefreshFailed = styled.p`
  font-size: var(--juno-font-size-body-sm);
  color: var(--juno-color-red);
  margin: 0 0 var(--juno-space-3);

  button {
    background: none;
    border: 0;
    padding: 0;
    font: inherit;
    color: var(--juno-color-purple);
    font-weight: var(--juno-font-weight-bold);
    text-decoration: underline;
    cursor: pointer;
  }
`;

const TableCard = styled.div`
  margin-bottom: var(--juno-space-6);

  ${mediaQueries.md} {
    background: var(--juno-color-surface);
    border: 1px solid var(--juno-color-pale-grey);
    border-radius: var(--juno-radius-lg);
    overflow: hidden;
  }
`;

// Wide tables scroll inside the card rather than the page.
const TableScroll = styled.div`
  overflow-x: auto;
`;

// Gutters and page padding are tight so the table fits a 1536px laptop without a sideways scroll.
const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: var(--referrals-font-size-sm);

  th {
    text-align: left;
    font-size: var(--referrals-font-size-xs);
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: var(--juno-color-light-grey);
    font-weight: var(--juno-font-weight-bold);
    padding: var(--referrals-header-padding) var(--referrals-cell-gutter);
    border-bottom: 1px solid var(--juno-color-pale-grey);
    white-space: nowrap;
  }

  td {
    padding: var(--juno-space-2) var(--referrals-cell-gutter);
    border-bottom: 1px solid var(--juno-color-very-pale-grey);
    white-space: nowrap;
    color: var(--referrals-text, var(--juno-color-dark-grey));
    vertical-align: top;
  }

  tr:last-child td {
    border-bottom: none;
  }

  /* Notes are the one free-text column, so they take up the slack: they wrap rather than pushing
     the table into a scroll, which every other column is short enough to avoid. */
  td[data-cell="notes"] {
    color: var(--referrals-text, var(--juno-color-text-secondary));
    white-space: normal;

    ${mediaQueries.md} {
      min-width: 9rem;
    }
  }

  ${mediaQueriesUntil.md} {
    font-size: var(--referrals-font-size-md);

    thead {
      display: none;
    }

    tbody {
      display: block;
    }

    td {
      padding: 0;
      border-bottom: none;
      white-space: normal;
      min-width: 0;
    }

    td:empty {
      display: none;
    }

    td[data-cell="client"],
    td[data-cell="phone"],
    td[data-cell="email"] {
      overflow-wrap: anywhere;
    }

    td[data-cell="client"] {
      order: 1;
      flex: 1;
    }

    td[data-cell="status"] {
      order: 2;
      margin-left: var(--juno-space-3);
    }

    /* The row's gap sits between every wrapped line, so each group's first cell carries only
       the difference up to the spacing the cards had. */
    td[data-cell="notes"] {
      order: 3;
      width: 100%;
      margin-top: calc(var(--referrals-gap) - var(--referrals-line-gap));
    }

    td[data-cell="phone"] {
      order: 4;
      width: 100%;
      margin-top: calc(var(--referrals-block-gap) - var(--referrals-line-gap));
    }

    td[data-cell="email"] {
      order: 5;
      width: 100%;
      margin-top: calc(var(--referrals-gap) - var(--referrals-line-gap));
    }

    /* "Referred 19 Aug · Sent by Luke S. · Last contact 19 Aug, 12:02 · £430k P". The separator
       trails each item so a wrapped line never starts with one. */
    td[data-cell="referred"] {
      order: 6;
      margin-top: calc(var(--referrals-block-gap) - var(--referrals-line-gap));
    }

    td[data-cell="sent-by"] {
      order: 7;
    }

    td[data-cell="last-contact"] {
      order: 8;
    }

    td[data-cell="price"] {
      order: 9;
    }

    td[data-cell="referred"],
    td[data-cell="sent-by"],
    td[data-cell="price"],
    td[data-cell="last-contact"] {
      font-size: var(--referrals-font-size-sm);
      color: var(--referrals-text, var(--juno-color-text-secondary));
    }

    td[data-title]::before {
      content: attr(data-title) " ";
    }

    td[data-cell="referred"]::after,
    td[data-cell="sent-by"]::after,
    td[data-cell="last-contact"]::after {
      content: "·";
      margin: 0 var(--referrals-separator-gap);
    }
  }
`;

const Row = styled.tr`
  td:first-child {
    border-left: 3px solid ${(props) => statusColour(props.$status)};
  }

  ${mediaQueriesUntil.md} {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    row-gap: var(--referrals-line-gap);
    margin-bottom: var(--juno-space-3);
    padding: var(--referrals-row-padding) var(--juno-space-4);
    background: var(--juno-color-surface);
    border: 1px solid var(--juno-color-pale-grey);
    border-left: 4px solid ${(props) => statusColour(props.$status)};
    border-radius: var(--juno-radius-lg);

    &:last-child {
      margin-bottom: 0;
    }

    td:first-child {
      border-left: none;
    }
  }

  ${(props) =>
    props.$status === "not_proceeding" && "--referrals-text: var(--juno-status-color-referral-not-proceeding-row);"}

  ${mediaQueries.md} {
    &:hover td {
      background: var(--juno-color-very-pale-grey);
    }
  }
`;

const ClientCell = styled.td`
  font-weight: var(--juno-font-weight-bold);
  min-width: 7rem;
`;

// Unusually long names and emails are cut short, full value on hover (and, for email, the copy
// button). Most are well under the caps.
const ClientName = styled.span`
  ${mediaQueries.md} {
    display: block;
    max-width: 11.25rem;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const Contact = styled.span`
  display: inline-flex;
  align-items: center;
  gap: var(--referrals-gap);
`;

const EmailText = styled.span`
  ${mediaQueries.md} {
    max-width: 13rem;
    overflow: hidden;
    text-overflow: ellipsis;

    /* Inside the clipped box, so the focus ring isn't cut off. */
    a:focus-visible {
      outline-offset: -2px;
    }
  }
`;

const CopyControl = styled.button`
  display: inline-flex;
  align-items: center;
  padding: var(--referrals-copy-padding);
  border: 0;
  background: none;
  color: var(--juno-color-light-grey);
  cursor: pointer;

  svg {
    width: 1rem;
    height: 1rem;
  }

  &:hover,
  &:focus-visible {
    color: var(--juno-color-purple);
  }
`;

// Looks like a plain heading until hovered.
const SortButton = styled.button`
  position: relative;
  padding: 0;
  border: 0;
  background: none;
  font: inherit;
  letter-spacing: inherit;
  text-transform: inherit;
  white-space: nowrap;
  cursor: pointer;
  color: ${(props) => (props.$active ? "var(--juno-color-off-black)" : "inherit")};

  &:hover,
  &:focus-visible {
    color: var(--juno-color-purple);
  }
`;

// Hangs into the cell padding so it adds no width.
const SortArrow = styled.span`
  position: absolute;
  left: calc(100% + 0.15rem);
  top: 50%;
  transform: translateY(-50%);
  font-size: 0.55rem;
`;

const DatePart = styled.span`
  white-space: nowrap;
`;

const ContactLink = styled.a`
  border-bottom: 1px solid transparent;
  color: var(--juno-color-link);
`;

// A pair of case links may wrap between the two, never inside one.
const CaseLink = styled(Link)`
  border-bottom: 1px solid transparent;
  white-space: nowrap;
  color: var(--juno-color-link);
`;

const CaseSeparator = styled.span`
  margin: 0 var(--referrals-separator-gap);
`;

const StatusPill = styled.span`
  display: inline-block;
  white-space: nowrap;
  font-size: var(--referrals-font-size-pill);
  font-weight: var(--juno-font-weight-bold);
  line-height: 1.4;
  padding: var(--referrals-pill-padding);
  border-radius: 1rem;
  ${(props) => {
    if (props.$status === "instructed") {
      return "background: var(--juno-status-color-referral-instructed-background); color: var(--juno-status-color-referral-instructed);";
    }
    if (props.$status === "contacting") {
      return "background: var(--juno-status-color-referral-contacting-background); color: var(--juno-status-color-referral-contacting);";
    }
    return "background: var(--juno-status-color-referral-not-proceeding-background); color: var(--juno-status-color-referral-not-proceeding);";
  }}
`;
